import {isEmpty} from 'lodash-es';
import React from 'react';

import type {BusinessTransactionChannel} from '@afriex/core/domain/enums';
import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {Button, CheckBox, Select} from '@/components';
import CountryPicker from '@/components/CountryPicker';
import Modal from '@/components/Modals';
import type {AfriexCountryCode} from '@/types';
import {AfriexCurrencyCode, readSettlementAccountInfo} from '@/types';
import {logRejection} from '@/utils/logRejection';

import SettlementAccountChannelFields from './SettlementAccountChannelFields';
import {
  defaultSettlementChannel,
  settlementChannelFormFields,
  settlementChannelForms,
  settlementChannelLabel,
  settlementSharedFields,
} from './settlementChannelForms';
import type {SettlementAccountAction} from './types';
import {SettlementAccountActionType} from './types';
import useSettlementAccountUpdate from './useSettlementAccountUpdate';

type SettlementAccountFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialData?: SettlementPaymentMethod;
  /** Called after a successful save so the list refreshes in place. */
  onSuccess?: () => void | Promise<void>;
};

// Built from the registry, so the picker cannot drift from the subform below.
const channelOptions = settlementChannelForms.map((form) => ({
  key: form.label,
  value: form.channel,
}));

const currencyOptions = Object.values(AfriexCurrencyCode).map((currency) => ({
  key: currency,
  value: currency,
}));

const SettlementAccountFormModal = ({
  isOpen,
  onClose,
  initialData,
  onSuccess,
}: SettlementAccountFormModalProps) => {
  const {
    account,
    dispatch,
    handleSubmit,
    isLoading,
    fieldError,
    clearFieldError,
  } = useSettlementAccountUpdate(onClose, initialData, onSuccess);

  const info = readSettlementAccountInfo(account.info);
  const channel = account.channel ?? defaultSettlementChannel;

  const isEditing = !isEmpty(initialData);
  const title = isEditing
    ? 'Update Settlement Account'
    : 'New Settlement Account';

  const update = (payload: SettlementAccountAction['payload']) => {
    // A message about a field stops being true the moment that field is edited.
    clearFieldError();
    dispatch({
      type: SettlementAccountActionType.UPDATE_SETTLEMENT_ACCOUNT,
      payload,
    });
  };

  const updateInfo = (partial: Record<string, string>) =>
    update({info: {...info, ...partial}});

  // `id` doubles as the label association and the anchor for the field's own
  // error message, which the caller renders below the control.
  const fieldAria = (field: string) => {
    const invalid = fieldError?.field === field;
    const id = `settlement-account-${field}`;
    return {
      id,
      'aria-invalid': invalid || undefined,
      'aria-describedby': invalid ? `${id}-error` : undefined,
    };
  };

  const inlineError = (field: string) =>
    fieldError?.field === field ? (
      <p
        id={`settlement-account-${field}-error`}
        className="mt-2 text-sm text-red-400"
        data-cy={`merchant-settlement-accounts-form-${field}-error`}
      >
        {fieldError.message}
      </p>
    ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title={title}
      dataCy="merchant-settlement-accounts-form"
    >
      <div className="pt-3 px-2">
        {/* The form stays mounted while saving rather than being swapped for a
            spinner: unmounting it dropped the operator's focus mid-request. */}
        <form
          aria-busy={isLoading}
          onSubmit={(e) => {
            e.preventDefault();
            // handleSubmit toasts its own failures, so this only fires if that
            // handler itself throws.
            handleSubmit(account).catch(
              logRejection('Failed to submit settlement account'),
            );
          }}
          className="flex flex-col gap-1"
        >
          <Select
            {...fieldAria('currencyCode')}
            label="Currency"
            dataCy="merchant-settlement-accounts-form-currencyCode"
            value={account.currencyCode ?? ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              update({
                currencyCode: e.target.value as AfriexCurrencyCode,
              })
            }
            options={currencyOptions}
            required
            className="w-full"
          />
          {inlineError('currencyCode')}

          <CountryPicker
            {...fieldAria('countryCode')}
            label="Country"
            dataCy="merchant-settlement-accounts-form-countryCode"
            placeholder="Select country"
            value={account.countryCode ?? ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              // The picker yields '' when nothing is chosen, which is not a
              // country code; store undefined so the field simply stays unset.
              update({
                countryCode: e.target.value
                  ? (e.target.value as AfriexCountryCode)
                  : undefined,
              })
            }
            className="w-full"
          />
          {inlineError('countryCode')}

          {/* The channel decides which details the subform asks for. Those are
              declared per channel in the registry, so this form never names
              one, and adding a channel changes nothing here. */}
          <Select
            {...fieldAria('channel')}
            label="Channel"
            dataCy="merchant-settlement-accounts-form-channel"
            value={channel}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              // Info keys are channel-specific, so switching starts the
              // details over rather than carrying the previous channel's
              // fields onto the method.
              update({
                channel: e.target.value as BusinessTransactionChannel,
                info: {},
              })
            }
            options={channelOptions}
            className="w-full"
          />
          {inlineError('channel')}

          {/* Grouped, and named after the channel: a screen reader then
              announces the set as a group when the channel swaps it out, and
              a sighted operator can see which channel they are filling in. */}
          <fieldset className="flex flex-col gap-1 border-0 p-0 m-0">
            <legend className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {settlementChannelLabel(channel)} details
            </legend>
            <SettlementAccountChannelFields
              fields={settlementChannelFormFields(channel)}
              info={account.info}
              error={fieldError}
              onChange={updateInfo}
            />
          </fieldset>

          {/* Shared by every channel, so it sits outside the channel's group. */}
          <SettlementAccountChannelFields
            fields={settlementSharedFields}
            info={account.info}
            error={fieldError}
            onChange={updateInfo}
          />

          {/* Archiving is how an account is retired: invoices already raised
              against it keep their copy of the details. */}
          <CheckBox
            label="Archived"
            checked={Boolean(account.meta?.flags?.isDeactivated)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              update({
                meta: {
                  ...account.meta,
                  flags: {isDeactivated: e.target.checked},
                },
              })
            }
          />

          <div className="mt-4 flex items-center justify-end gap-3">
            {/* `role="status"` is announced politely, and unlike the old
                spinner swap it leaves the form and its values in place. */}
            {isLoading && (
              <span
                role="status"
                className="mr-auto text-sm text-gray-500 dark:text-gray-400"
              >
                Saving...
              </span>
            )}
            <Button
              onClick={onClose}
              colorScheme="cyan"
              variant="ghost"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="cyan"
              disabled={isLoading}
              dataCy="merchant-settlement-accounts-form-submit"
            >
              {isEditing ? 'Update' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default SettlementAccountFormModal;
